import {Box} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/Box';
import {ComponentDesc} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/component/ComponentDesc';
import {IProperty} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/Prop';
import {SystemPropsConfigs} from '@mintdata-wsp/common/state/ui/visualDesigner/surface/property/SystemPropsConfigs';
import {EventSpec} from '@mintdata/mint-kit/api/events/EventSpec';
import {EventPropName} from '@mintdata/mint-kit/api/events/EventType';
import {BindingId, EventPropId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Assert} from '@mintdata/mint-kit/impl/util/Assert';
import {IdUtil} from '@mintdata/mint-kit/impl/util/IDUtil';
import {action, computed, observable} from 'mobx';
import {createTransformer, ITransformer} from 'mobx-utils';

export interface IEventPropSnapshot
{
    id:EventPropId;
    name:EventPropName;
    isEnabled:boolean;
    bindingId:BindingId;
}

export class EventProp implements IProperty
{

    public id:EventPropId;

    @observable
    public name:EventPropName;

    public box:Box;

    @observable
    public isEnabled:boolean;

    @observable
    public bindingId:BindingId;

    private constructor()
    {
    }

    public static create(box:Box,
                         name:EventPropName,
                         isEnabled:boolean):EventProp
    {
        const eventProp:EventProp = new EventProp();

        eventProp.id = IdUtil.newGUID();
        eventProp.box = box;
        eventProp.name = name;
        eventProp.isEnabled = isEnabled;
        eventProp.bindingId = null;

        return eventProp;
    }

    public clone(box:Box):EventProp
    {
        const eventProp:EventProp = EventProp.create(box, this.name, this.isEnabled);

        // NOTE: binding will be replaced in `SurfaceAreaController.cloneBox` method,
        //  which ultimately is the the only one calling this `Prop.clone`
        eventProp.bindingId = this.bindingId;

        return eventProp;
    }

    public get componentDesc():ComponentDesc
    {
        return this.box && this.box.componentDesc;
    }

    @computed
    public get spec():EventSpec
    {
        if( !this.box )
        {
            const spec = SystemPropsConfigs.surfaceEventsMap.findByPropName(this.name);
            if( spec )
                return spec;
        }

        return this.componentDesc && this.componentDesc.getEventPropSpec(this.name);
    }

    @computed
    public get displayName():string
    {
        if( this.name === SystemPropsConfigs.APPLICATION_LOAD_EVENT_PROP_NAME )
            return 'onApplicationLoaded';

        if( this.name === SystemPropsConfigs.VIEWPORT_RESIZE_EVENT_PROP_NAME )
            return 'onViewportResized';

        return this.name;
    }

    @action.bound
    public setIsEnabled(isEnabled:boolean):void
    {
        this.isEnabled = isEnabled;
    }

    public get bindingIds():Array<BindingId>
    {
        return this.bindingId ? [this.bindingId] : [];
    }

    @action.bound
    public addBinding(bindingId:BindingId):boolean
    {
        this.bindingId = bindingId;
        return true;
    }

    @action.bound
    public removeBinding(bindingId:BindingId):boolean
    {
        if( this.bindingId === bindingId )
        {
            this.bindingId = null;
            return true;
        }

        return false;
    }

    @action.bound
    public clearBindings()
    {
        this.bindingId = null;
    }

    public static getSurfaceEventPropNames():Array<string>
    {
        return [
            SystemPropsConfigs.APPLICATION_LOAD_EVENT_PROP_NAME,
            SystemPropsConfigs.VIEWPORT_RESIZE_EVENT_PROP_NAME,
            SystemPropsConfigs.POST_MESSAGE_PROP_NAME
        ];
    }

    public static createSurfaceEventProps():Map<EventPropId, EventProp>
    {
        const eventProps:Map<EventPropId, EventProp> = new Map();
        this.getSurfaceEventPropNames().forEach(eventPropName =>
        {
            const eventProp = this.createSurfaceEventProp(eventPropName);
            eventProps.set(eventProp.id, eventProp);
        });

        return eventProps;
    }

    public static createSurfaceEventProp(eventPropName:string):EventProp
    {
        const surfaceEventSpec:EventSpec =
            SystemPropsConfigs.surfaceEventsMap.findByPropName(eventPropName);
        Assert.yes(surfaceEventSpec, `invalid eventPropName ${eventPropName}`);

        const surfaceEventProp:EventProp = EventProp.create(
            null,
            surfaceEventSpec.eventPropertyName,
            surfaceEventSpec.isEnabled
        );
        return surfaceEventProp;
    }

    public static toJson:ITransformer<EventProp, IEventPropSnapshot> = createTransformer((eventProp:EventProp) =>
    {
        return eventProp.bindingId
            ? {
                id: eventProp.id,
                name: eventProp.name,
                isEnabled: eventProp.isEnabled,
                bindingId: eventProp.bindingId
            }
            : null;
    });

    public static fromJson(json:IEventPropSnapshot, box:Box):EventProp
    {
        const eventProp:EventProp = new EventProp();

        eventProp.id = json.id;
        eventProp.name = json.name;
        eventProp.isEnabled = json.isEnabled;
        eventProp.bindingId = json.bindingId;

        eventProp.box = box;

        return eventProp;
    }

    public static fromJsonDeep(json:IEventPropSnapshot, box:Box):EventProp
    {
        return EventProp.fromJson(json, box);
    }
}
